"use client";

import Link from "next/link";
import React from "react";
import styled from "styled-components";

import type { Breadcrumb as BreadcrumbType } from "../../types/category.type";
import { Text, TextColor, TextSize } from "../Text";

type BreadcrumbProps = {
  breadcrumbs: BreadcrumbType[];
  loadingCategory?: boolean;
};

const Breadcrumb: React.FC<BreadcrumbProps> = ({
  breadcrumbs,
  loadingCategory = false,
}) => {
  if (loadingCategory) {
    return (
      <BreadcrumbContainer>
        <BreadcrumbItem>
          <SkeletonText />
        </BreadcrumbItem>
        <BreadcrumbItem>
          <SkeletonText />
        </BreadcrumbItem>
      </BreadcrumbContainer>
    );
  }

  if (!breadcrumbs || breadcrumbs.length === 0) return null;

  // Odfiltrowanie unikalnych title
  breadcrumbs = breadcrumbs.filter(
    (breadcrumb, index, self) =>
      index === self.findIndex((t) => t.title === breadcrumb.title),
  );

  const buildHref = (index: number): string => {
    const currentBreadcrumb = breadcrumbs[index];

    if (currentBreadcrumb?.inheritPath === false) {
      let slug = currentBreadcrumb.slug;
      // Zabezpieczenie przed /strona-glowna
      if (slug === "strona-glowna" || slug === "/strona-glowna") {
        return "/";
      }
      // Zamiana odziez-dziecieca na dziecko
      if (slug === "odziez-dziecieca") {
        slug = "dziecko";
      }
      return `/${slug}`;
    }

    if (index === 0 && breadcrumbs?.[0]?.slug === "/") {
      return "/";
    }

    // Zabezpieczenie dla strony głównej na pierwszej pozycji
    if (
      index === 0 &&
      (breadcrumbs?.[0]?.slug === "strona-glowna" ||
        breadcrumbs?.[0]?.slug === "/strona-glowna")
    ) {
      return "/";
    }

    if (breadcrumbs.length > 3) {
      if (index === breadcrumbs.length - 1) {
        let firstSlug =
          breadcrumbs[1]?.slug === "strona-glowna" ? "" : breadcrumbs[1]?.slug;
        let currentSlug =
          breadcrumbs[index]?.slug === "strona-glowna"
            ? ""
            : breadcrumbs[index]?.slug;

        // Zamiana odziez-dziecieca na dziecko
        if (firstSlug === "odziez-dziecieca") firstSlug = "dziecko";
        if (currentSlug === "odziez-dziecieca") currentSlug = "dziecko";

        if (!firstSlug && !currentSlug) return "/";
        if (!firstSlug) return `/${currentSlug}`;
        if (!currentSlug) return `/${firstSlug}`;

        return `/${firstSlug}/${currentSlug}`;
      } else if (index > 1) {
        let firstSlug =
          breadcrumbs[1]?.slug === "strona-glowna" ? "" : breadcrumbs[1]?.slug;
        let currentSlug =
          breadcrumbs[index]?.slug === "strona-glowna"
            ? ""
            : breadcrumbs[index]?.slug;

        // Zamiana odziez-dziecieca na dziecko
        if (firstSlug === "odziez-dziecieca") firstSlug = "dziecko";
        if (currentSlug === "odziez-dziecieca") currentSlug = "dziecko";

        if (!firstSlug && !currentSlug) return "/";
        if (!firstSlug) return `/${currentSlug}`;
        if (!currentSlug) return `/${firstSlug}`;

        return `/${firstSlug}/${currentSlug}`;
      }
    }

    const relevantBreadcrumbs = breadcrumbs.slice(1, index + 1);
    const filteredSlugs = relevantBreadcrumbs
      .map((breadcrumb) => breadcrumb.slug)
      .filter((slug) => slug !== "strona-glowna" && slug !== "/strona-glowna")
      .map((slug) => (slug === "odziez-dziecieca" ? "dziecko" : slug));

    if (filteredSlugs.length === 0) {
      return "/";
    }

    return "/" + filteredSlugs.join("/");
  };

  // Na mobile: pokaż tylko drugą i ostatnią kategorię (pomijamy "Strona główna")
  const getMobileBreadcrumbs = () => {
    if (breadcrumbs.length <= 2) {
      return breadcrumbs;
    }

    const secondCategory = breadcrumbs[1];
    const lastCategory = breadcrumbs[breadcrumbs.length - 1];

    // Jeśli druga i ostatnia kategoria to ta sama, pokaż tylko ostatnią
    if (
      secondCategory?.slug === lastCategory?.slug &&
      secondCategory?.title === lastCategory?.title
    ) {
      return [lastCategory];
    }

    // Druga kategoria (indeks 1) i ostatnia
    return [secondCategory, lastCategory];
  };

  const mobileBreadcrumbs = getMobileBreadcrumbs();

  return (
    <BreadcrumbContainer>
      <DesktopView>
        {breadcrumbs.map((breadcrumb, index) => (
          <BreadcrumbItem key={index}>
            <Link href={buildHref(index)}>
              <Text
                text={breadcrumb.title}
                color={TextColor.GRAYSCALE_900}
                fontSize={TextSize.XS}
              />
            </Link>
          </BreadcrumbItem>
        ))}
      </DesktopView>

      <MobileView>
        {mobileBreadcrumbs.map((breadcrumb, mobileIndex) => {
          const originalIndex = breadcrumbs.findIndex(
            (b) => b.slug === breadcrumb?.slug && b.title === breadcrumb?.title,
          );

          return (
            <BreadcrumbItem
              key={`mobile-${breadcrumb?.slug}-${breadcrumb?.title}-${mobileIndex}`}
            >
              <Link href={buildHref(originalIndex)}>
                <Text
                  text={breadcrumb?.title}
                  color={TextColor.GRAYSCALE_900}
                  fontSize={TextSize.XS}
                />
              </Link>
            </BreadcrumbItem>
          );
        })}
      </MobileView>
    </BreadcrumbContainer>
  );
};

const BreadcrumbContainer = styled.div`
  display: flex;
  align-items: center;
`;

const DesktopView = styled.div`
  display: none;
  align-items: center;

  @media (min-width: 768px) {
    display: flex;
  }
`;

const MobileView = styled.div`
  display: flex;
  align-items: center;

  @media (min-width: 768px) {
    display: none;
  }
`;

const BreadcrumbItem = styled.div`
  display: flex;
  align-items: center;

  &:not(:last-child) {
    margin-right: 0.5rem;

    &::after {
      content: ">";
      color: ${({ theme }) => theme.colors.GRAYSCALE_400};
      margin-left: 0.5rem;
      font-size: 0.75rem;
    }

    @media (min-width: 768px) {
      margin-right: 0.75rem;

      &::after {
        margin-left: 0.75rem;
      }
    }
  }

  a {
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const SkeletonText = styled.div`
  height: 1rem;
  width: 60px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;

  @media (min-width: 768px) {
    width: 80px;
  }

  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

export default Breadcrumb;
